import { Mic01Icon } from "@hugeicons-pro/core-duotone-rounded";
import { CloudUploadIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { EpisodeUploadDialog } from "~/components/episode-upload-dialog";
import { DeleteEpisodeDialog } from "~/components/episodes/delete-episode-dialog";
import { AppContainer } from "~/components/layouts/app/app-container";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { CardLoadingIndicator } from "~/components/ui/card-loading-indicator";
import { DataTable } from "~/components/ui/data-table";
import { PageHead } from "~/components/ui/page-head";
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from "~/components/ui/table";
import { useFilters } from "~/hooks/use-filters";
import { sortByToState, stateToSortBy } from "~/lib/table";
import { canAddEpisodesToChannel } from "~/models/channel";
import { type Episode, EpisodeStatus } from "~/models/episode";
import { formatEpisodeNumber } from "~/models/episode.helpers";
import { getMonetizationStatusFilters } from "~/models/monetization";
import type { Filters } from "~/tables";
import { getEpisodeColumns, getEpisodeStatusFilters } from "~/tables/episodes";
import { useTRPC } from "~/trpc/client";

type EpisodeFilters = Filters<Episode>;

export const Route = createFileRoute("/_app/podcasts/$channelUid/episodes/")({
	component: Page,
	async loader({ context, params }) {
		context.queryClient.prefetchQuery(
			context.trpc.episode.list.queryOptions({
				channelUid: params.channelUid,
			}),
		);
	},
	validateSearch: () => ({}) as EpisodeFilters,
});

function Page() {
	const { channelUid } = Route.useParams();
	const trpc = useTRPC();
	const { t } = useLingui();

	const [isOpenUploadDialog, setIsOpenUploadDialog] = useState(false);
	const [selectedChannelEpisode, setSelectedChannelEpisode] = useState<Episode | null>(null);
	const [selectedAction, setSelectedAction] = useState<"delete" | null>(null);

	const { data: channel } = useSuspenseQuery(
		trpc.channel.find.queryOptions({
			channelUid,
		}),
	);
	const { data: episodes, isFetching } = useQuery(
		trpc.episode.list.queryOptions(
			{
				channelUid,
			},
			{
				refetchInterval(query) {
					if (query.state.data?.some((episode) => episode.statusID === EpisodeStatus.Transcoding)) {
						return 1000 * 5; // 5 seconds
					}

					return false;
				},
			},
		),
	);

	const { columnFilters, filters, resetFilters, setFilters } = useFilters(Route.id);

	const paginationState = {
		pageIndex: filters.pageIndex ?? DEFAULT_PAGE_INDEX,
		pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
	};
	const sortingState = sortByToState(filters.sortBy);

	const canAddEpisodes = canAddEpisodesToChannel(channel);

	function onAction(action: string, episode: Episode) {
		switch (action) {
			case "delete": {
				setSelectedAction("delete");
				setSelectedChannelEpisode(episode);
				break;
			}
		}
	}

	return (
		<>
			<PageHead
				icon={Mic01Icon}
				title={t`Podcast Episodes`}
				subtitle={t`Manage your podcast episodes.`}
				action={
					<Button className="w-fit" variant="outline" onClick={() => setIsOpenUploadDialog(true)}>
						<HugeiconsIcon icon={CloudUploadIcon} aria-hidden="true" className="size-4" />
						<Trans>Upload a new episode</Trans>
					</Button>
				}
			/>
			<AppContainer className="my-12 flex flex-col gap-6">
				<EpisodeUploadDialog
					canAddEpisodes={canAddEpisodes}
					channel={channel}
					isOpen={isOpenUploadDialog}
					setIsOpen={setIsOpenUploadDialog}
				/>
				<Card>
					<CardContent className="relative px-0 sm:px-6">
						{isFetching && <CardLoadingIndicator />}
						<DataTable
							columns={getEpisodeColumns({ canAddEpisodes, onAction })}
							data={episodes ?? []}
							pagination={paginationState}
							filters={columnFilters}
							onFilterChange={(filters) => setFilters(filters)}
							sorting={sortingState}
							onSortingChange={(updaterOrValue) => {
								const newSortingState =
									typeof updaterOrValue === "function"
										? updaterOrValue(sortingState)
										: updaterOrValue;
								return setFilters({ sortBy: stateToSortBy(newSortingState) });
							}}
							meta={{
								search: {
									columnId: "title",
									placeholder: t`Search episodes..`,
								},
								filters: {
									seasonNum: {
										type: "faceted",
										title: t`Season`,
										options: episodes
											? Array.from(new Set(episodes.map((episode) => episode.seasonNum)))
													.sort()
													.filter((seasonNum) => seasonNum !== null)
													.map((seasonNum) => ({
														value: formatEpisodeNumber(seasonNum, "season"),
														label: t`Season ${seasonNum}`,
													}))
											: [],
									},
									status: {
										type: "faceted",
										title: t`Status`,
										options: getEpisodeStatusFilters(),
									},
									monetizationStatusID: {
										type: "faceted",
										title: t`Monetization`,
										options: getMonetizationStatusFilters(),
									},
								},
								resetFilters,
								onFilterChange: (filters) => setFilters(filters),
							}}
						/>
					</CardContent>
				</Card>
			</AppContainer>

			<DeleteEpisodeDialog
				controlled
				episodeUid={selectedChannelEpisode?.uid}
				open={!!selectedChannelEpisode && selectedAction === "delete"}
				onOpenChange={() => {
					setSelectedAction(null);
					setSelectedChannelEpisode(null);
				}}
				onSuccess={() => {
					setSelectedAction(null);
					setSelectedChannelEpisode(null);
				}}
			/>
		</>
	);
}
